/**
 * Campaign Wizard — the user-direct front door for compound campaign creation.
 *
 * Four steps: Settings → Targeting → Ad groups & ads → Review.
 * The Review step calls proposeCampaign (server resolves locations, dry-runs the
 * ENTIRE build atomically via validate_only, drafts source:'direct') and shows
 * the validation verdict + resolved geo targets; "Create campaign (paused)" then
 * applies THAT draft (applyCampaignDraft) — the review step itself is the human
 * gate (same model as the confirm modal for single ops). The campaign is created
 * PAUSED with everything inside; the user enables it to go live.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { proposeCampaign, applyCampaignDraft, decideChangeSet } from './googleAdsApi';

// One responsive search ad within an ad group (F14: up to 3 per ad group).
interface WizardAd {
  finalUrl: string;
  path1: string;
  path2: string;
  headlines: string[];
  descriptions: string[];
}
interface WizardAdGroup {
  name: string;
  maxCpc: string;
  matchType: string;             // ad-group DEFAULT match type (per-line syntax overrides)
  keywordsText: string;          // one keyword per line; optional inline override
  ads: WizardAd[];               // 1–3 ads
}

const MAX_ADS_PER_GROUP = 3;
const newAd = (): WizardAd => ({ finalUrl: '', path1: '', path2: '', headlines: ['', '', ''], descriptions: ['', ''] });
const newAdGroup = (): WizardAdGroup => ({
  name: '', maxCpc: '', matchType: 'PHRASE', keywordsText: '',
  ads: [newAd()],
});

// Parse one keyword line into { text, matchType }. Inline override syntax:
//   "plumber | exact"   → EXACT
//   "drain repair [phrase]" → PHRASE   (also "[broad]", "[exact]")
//   "blocked drain"     → falls back to the ad-group default match type.
// Returns null for an empty line.
function parseKeywordLine(line: string, defaultMatch: string): { text: string; matchType: string } | null {
  let s = line.trim();
  if (!s) return null;
  let matchType = '';
  // "[phrase]" / "[exact]" / "[broad]" trailing bracket form
  const bracket = s.match(/\[(exact|phrase|broad)\]\s*$/i);
  if (bracket) { matchType = bracket[1].toUpperCase(); s = s.slice(0, bracket.index).trim(); }
  else {
    // "keyword | exact" pipe form
    const pipe = s.match(/\|\s*(exact|phrase|broad)\s*$/i);
    if (pipe) { matchType = pipe[1].toUpperCase(); s = s.slice(0, pipe.index).trim(); }
  }
  if (!s) return null;
  return { text: s, matchType: matchType || defaultMatch };
}

const DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

// Common Google Ads languageConstant ids (server accepts numeric ids only).
const COMMON_LANGUAGES: Array<{ id: string; name: string }> = [
  { id: '1000', name: 'English' }, { id: '1003', name: 'Spanish' }, { id: '1001', name: 'German' },
  { id: '1002', name: 'French' }, { id: '1004', name: 'Italian' }, { id: '1014', name: 'Portuguese' },
  { id: '1010', name: 'Dutch' }, { id: '1005', name: 'Japanese' }, { id: '1012', name: 'Korean' },
  { id: '1017', name: 'Chinese (simpl.)' }, { id: '1019', name: 'Arabic' }, { id: '1031', name: 'Russian' },
];
const IS_LOCATIONS: Array<{ v: string; label: string }> = [
  { v: 'ANYWHERE_ON_PAGE', label: 'anywhere on page' },
  { v: 'TOP_OF_PAGE', label: 'top of page' },
  { v: 'ABSOLUTE_TOP_OF_PAGE', label: 'absolute top' },
];

export const CampaignWizard: React.FC<{ customerId: string; currency?: string; onClose: () => void }> = ({ customerId, currency, onClose }) => {
  const [step, setStep] = useState(0);
  // Step 1 — settings
  const [name, setName] = useState('');
  const [dailyBudget, setDailyBudget] = useState('');
  const [strategy, setStrategy] = useState('MANUAL_CPC');
  const [targetCpa, setTargetCpa] = useState('');
  const [targetRoas, setTargetRoas] = useState('');
  const [enhancedCpc, setEnhancedCpc] = useState(false);
  const [maxCpcCeiling, setMaxCpcCeiling] = useState('');
  const [tisLocation, setTisLocation] = useState('ANYWHERE_ON_PAGE');
  const [tisFraction, setTisFraction] = useState('');
  const [searchPartners, setSearchPartners] = useState(false);
  // Step 2 — targeting
  const [worldwide, setWorldwide] = useState(false);
  const [locationsText, setLocationsText] = useState('');
  const [countryCode, setCountryCode] = useState('AU');
  const [languageIds, setLanguageIds] = useState<string[]>(['1000']);
  const [schedule, setSchedule] = useState<Array<{ day: string; start: string; end: string }>>([]);
  // Step 3 — ad groups
  const [adGroups, setAdGroups] = useState<WizardAdGroup[]>([newAdGroup()]);
  // Step 4 — review
  const [draft, setDraft] = useState<{ id: string; summary: string; validationOk: boolean; validationErrors: string[]; locationNotes: string[] } | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [done, setDone] = useState(false);
  // Reject an abandoned (drafted-but-not-created) set on unmount — covers
  // Cancel, overlay click, AND route/account changes that unmount us directly.
  const draftIdRef = useRef<string | null>(null);
  const doneRef = useRef(false);
  // Track an in-flight apply so the unmount-reject never races a create that has
  // already claimed the set ('applying') — a racing reject would otherwise strand
  // a created campaign as 'rejected' and block undo. (server-side belt is the
  // 'applying' guard in rejectChangeSet.)
  const creatingRef = useRef(false);
  useEffect(() => { draftIdRef.current = draft?.id || null; }, [draft]);
  useEffect(() => { doneRef.current = done; }, [done]);
  useEffect(() => { creatingRef.current = creating; }, [creating]);
  useEffect(() => () => {
    if (draftIdRef.current && !doneRef.current && !creatingRef.current) void decideChangeSet(draftIdRef.current, 'reject');
  }, []);

  const patchAg = useCallback((i: number, patch: Partial<WizardAdGroup>) => {
    setAdGroups((prev) => prev.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  }, []);

  // Patch a single ad (index ai) inside ad group i. F14.
  const patchAd = useCallback((i: number, ai: number, patch: Partial<WizardAd>) => {
    setAdGroups((prev) => prev.map((g, j) => (j === i
      ? { ...g, ads: g.ads.map((a, k) => (k === ai ? { ...a, ...patch } : a)) }
      : g)));
  }, []);

  const lines = (t: string) => t.split('\n').map((s) => s.trim()).filter(Boolean);

  // Client-side gate per step (server re-validates everything authoritatively).
  const stepError = (s: number): string | null => {
    if (s === 0) {
      if (!name.trim()) return 'Campaign name is required.';
      const b = parseFloat(dailyBudget);
      if (!isFinite(b) || b < 1) return 'Daily budget must be at least 1.';
      if (strategy === 'MAXIMIZE_CONVERSIONS' && targetCpa.trim() && !(parseFloat(targetCpa) > 0)) return 'Target CPA must be a positive number (or blank).';
      if (strategy === 'MAXIMIZE_CONVERSION_VALUE' && targetRoas.trim() && !(parseFloat(targetRoas) > 0)) return 'Target ROAS must be a positive number (or blank).';
      if ((strategy === 'MAXIMIZE_CLICKS' || strategy === 'TARGET_IMPRESSION_SHARE') && maxCpcCeiling.trim() && !(parseFloat(maxCpcCeiling) > 0)) return 'Max CPC ceiling must be a positive number (or blank).';
      if (strategy === 'TARGET_IMPRESSION_SHARE' && !(parseFloat(tisFraction) > 0 && parseFloat(tisFraction) <= 100)) return 'Target impression share must be a percentage from 1 to 100 (e.g. 65).';
      return null;
    }
    if (s === 1) {
      if (!worldwide && !lines(locationsText).length) return 'Add at least one location (e.g. "Adelaide, South Australia"), or tick "target worldwide".';
      if (!worldwide && !/^[A-Z]{2}$/.test(countryCode.trim().toUpperCase())) return 'Country bias must be a 2-letter code (e.g. AU).';
      if (!languageIds.length) return 'Select at least one language.';
      for (const r of schedule) {
        const sh = parseInt(r.start, 10); const eh = parseInt(r.end, 10);
        if (!isFinite(sh) || !isFinite(eh) || sh < 0 || eh > 24 || sh >= eh) return `Schedule ${r.day.toLowerCase()}: need 0 ≤ start < end ≤ 24.`;
      }
      return null;
    }
    if (s === 2) {
      for (let i = 0; i < adGroups.length; i++) {
        const g = adGroups[i];
        const label = g.name.trim() || `#${i + 1}`;
        if (!g.name.trim()) return `Ad group #${i + 1} needs a name.`;
        // Keywords parse with optional per-line match-type override.
        const kws = lines(g.keywordsText).map((l) => parseKeywordLine(l, g.matchType)).filter(Boolean);
        if (!kws.length) return `Ad group "${label}" needs at least one keyword (one per line).`;
        if (g.maxCpc.trim() && !(parseFloat(g.maxCpc) > 0)) return `Ad group "${label}": max CPC must be positive (or blank).`;
        // 1–3 ads, each validated independently. F14.
        if (g.ads.length < 1) return `Ad group "${label}" needs at least one ad.`;
        if (g.ads.length > MAX_ADS_PER_GROUP) return `Ad group "${label}": at most ${MAX_ADS_PER_GROUP} ads.`;
        for (let ai = 0; ai < g.ads.length; ai++) {
          const a = g.ads[ai];
          const an = g.ads.length > 1 ? ` ad ${ai + 1}` : ' ad';
          if (!/^https?:\/\/.+\..+/.test(a.finalUrl.trim())) return `Ad group "${label}":${an} needs a valid landing-page URL.`;
          if (a.headlines.filter((h) => h.trim()).length < 3) return `Ad group "${label}":${an} needs at least 3 headlines.`;
          if (a.descriptions.filter((d) => d.trim()).length < 2) return `Ad group "${label}":${an} needs at least 2 descriptions.`;
          if (a.headlines.some((h) => h.trim().length > 30)) return `Ad group "${label}":${an} has a headline over 30 characters.`;
          if (a.descriptions.some((d) => d.trim().length > 90)) return `Ad group "${label}":${an} has a description over 90 characters.`;
          const hs = a.headlines.map((h) => h.trim().toLowerCase()).filter(Boolean);
          if (new Set(hs).size !== hs.length) return `Ad group "${label}":${an} headlines must be unique.`;
          const ds = a.descriptions.map((d) => d.trim().toLowerCase()).filter(Boolean);
          if (new Set(ds).size !== ds.length) return `Ad group "${label}":${an} descriptions must be unique.`;
        }
      }
      return null;
    }
    return null;
  };

  const buildSpec = () => ({
    name: name.trim(),
    dailyBudget: parseFloat(dailyBudget),
    bidding: {
      strategy,
      ...(strategy === 'MAXIMIZE_CONVERSIONS' && targetCpa.trim() ? { targetCpa: parseFloat(targetCpa) } : {}),
      ...(strategy === 'MAXIMIZE_CONVERSION_VALUE' && targetRoas.trim() ? { targetRoas: parseFloat(targetRoas) } : {}),
      ...(strategy === 'MANUAL_CPC' && enhancedCpc ? { enhancedCpc: true } : {}),
      ...((strategy === 'MAXIMIZE_CLICKS' || strategy === 'TARGET_IMPRESSION_SHARE') && maxCpcCeiling.trim() ? { maxCpcCeiling: parseFloat(maxCpcCeiling) } : {}),
      // Send an explicit 0–1 fraction so the percent input is never ambiguous
      // (e.g. "1" = 1% → 0.01, "100" = 100% → 1.0).
      ...(strategy === 'TARGET_IMPRESSION_SHARE' ? { location: tisLocation, targetFraction: parseFloat(tisFraction) / 100 } : {}),
    },
    searchPartners,
    countryCode: countryCode.trim().toUpperCase() || 'AU',
    worldwide,
    locations: worldwide ? [] : lines(locationsText),
    languages: languageIds.map((id) => ({ id, name: COMMON_LANGUAGES.find((l) => l.id === id)?.name || '' })),
    adSchedule: schedule.map((r) => ({ day: r.day, startHour: parseInt(r.start, 10), endHour: parseInt(r.end, 10) })),
    adGroups: adGroups.map((g) => ({
      name: g.name.trim(),
      ...(g.maxCpc.trim() ? { maxCpc: parseFloat(g.maxCpc) } : {}),
      // Per-keyword match type via inline syntax, falling back to the ad-group default. F14.
      keywords: lines(g.keywordsText)
        .map((l) => parseKeywordLine(l, g.matchType))
        .filter((k): k is { text: string; matchType: string } => !!k)
        .map((k) => ({ text: k.text, matchType: k.matchType })),
      // Up to 3 ads per ad group. F14.
      ads: g.ads.map((a) => ({
        finalUrl: a.finalUrl.trim(),
        headlines: a.headlines.map((h) => h.trim()).filter(Boolean),
        descriptions: a.descriptions.map((d) => d.trim()).filter(Boolean),
        ...(a.path1.trim() ? { path1: a.path1.trim() } : {}),
        ...(a.path1.trim() && a.path2.trim() ? { path2: a.path2.trim() } : {}),
      })),
    })),
  });

  const goNext = useCallback(async () => {
    const err = stepError(step);
    if (err) { toast.error(err); return; }
    if (step < 2) { setStep(step + 1); return; }
    // Entering Review: draft + dry-run server-side. A previous (stale) draft is rejected.
    setDrafting(true);
    try {
      if (draft?.id) { void decideChangeSet(draft.id, 'reject'); setDraft(null); }
      const r = await proposeCampaign(customerId, buildSpec());
      if (!r.ok || !r.changeSet) throw new Error(r.error || 'draft failed');
      setDraft({
        id: r.changeSet.id,
        summary: r.changeSet.summary || '',
        validationOk: r.changeSet.validation?.ok === true,
        validationErrors: r.changeSet.validation?.errors || [],
        locationNotes: r.locationNotes || [],
      });
      setStep(3);
    } catch (e: any) {
      toast.error(`Couldn't validate the campaign: ${e?.message || 'error'}`);
    } finally {
      setDrafting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, name, dailyBudget, strategy, targetCpa, targetRoas, enhancedCpc, maxCpcCeiling, tisLocation, tisFraction, searchPartners, worldwide, locationsText, countryCode, languageIds, schedule, adGroups, draft, customerId]);

  const create = useCallback(async () => {
    if (!draft?.id || !draft.validationOk) return;
    setCreating(true);
    try {
      const r = await applyCampaignDraft(customerId, draft.id);
      if (!r.ok) throw new Error(r.error || 'create failed');
      setDone(true);
      toast.success('Campaign created PAUSED in Google Ads. Enable it when you\'re ready to go live.');
    } catch (e: any) {
      toast.error(`Couldn't create the campaign: ${e?.message || 'error'}`);
    } finally {
      setCreating(false);
    }
  }, [draft, customerId]);

  // Rejection of an unapplied draft happens in the unmount effect (single path).
  const close = useCallback(() => { onClose(); }, [onClose]);

  const counter = (text: string, max: number) => (
    <span className={`gads-rsa__count${text.trim().length > max ? ' gads-rsa__count--over' : ''}`}>{text.trim().length}/{max}</span>
  );

  // Overlay tap must not silently discard a half-built campaign — confirm when
  // anything meaningful has been entered.
  const overlayClose = () => {
    if (drafting || creating) return;
    const dirty = !done && (name.trim() || dailyBudget.trim() || locationsText.trim() || adGroups.some((g) => g.name.trim() || g.keywordsText.trim() || g.ads.some((a) => a.finalUrl.trim())));
    if (dirty && !window.confirm('Discard this campaign draft?')) return;
    close();
  };

  return (
    <div className="gads-neg-modal__overlay" onClick={overlayClose}>
      <div className="gads-wizard" onClick={(e) => e.stopPropagation()}>
        <div className="gads-neg-modal__title">
          New Search campaign · step {step + 1}/4 — {['settings', 'targeting', 'ad groups & ads', 'review'][step]}
        </div>

        {step === 0 && (
          <>
            <div className="gads-rsa__section">
              <label className="gads-rsa__label">Campaign name</label>
              <input type="text" maxLength={255} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Emergency Plumbing — Adelaide" className="gads-neg-modal__input gads-rsa__wide" autoFocus />
            </div>
            <div className="gads-rsa__section">
              <label className="gads-rsa__label">Daily budget ({currency || 'account currency'})</label>
              <input type="number" inputMode="decimal" min="1" step="1" value={dailyBudget} onChange={(e) => setDailyBudget(e.target.value)} placeholder="50" className="gads-neg-modal__input" />
            </div>
            <div className="gads-rsa__section">
              <label className="gads-rsa__label">Bidding</label>
              <select value={strategy} onChange={(e) => setStrategy(e.target.value)} className="gads-neg-modal__input">
                <option value="MANUAL_CPC">Manual CPC</option>
                <option value="MAXIMIZE_CLICKS">Maximize Clicks</option>
                <option value="MAXIMIZE_CONVERSIONS">Maximize Conversions</option>
                <option value="MAXIMIZE_CONVERSION_VALUE">Maximize Conversion Value</option>
                <option value="TARGET_IMPRESSION_SHARE">Target Impression Share</option>
              </select>
              {strategy === 'MANUAL_CPC' && (
                <label className="gads-wizard__check" style={{ marginTop: 6 }}><input type="checkbox" checked={enhancedCpc} onChange={(e) => setEnhancedCpc(e.target.checked)} /> enhanced CPC (eCPC)</label>
              )}
              {strategy === 'MAXIMIZE_CONVERSIONS' && (
                <span className="gads-wizard__inline">
                  <input type="number" inputMode="decimal" min="0" step="0.5" value={targetCpa} onChange={(e) => setTargetCpa(e.target.value)} placeholder="target CPA (optional)" className="gads-neg-modal__input" />
                </span>
              )}
              {strategy === 'MAXIMIZE_CONVERSION_VALUE' && (
                <span className="gads-wizard__inline">
                  <input type="number" inputMode="decimal" min="0" step="0.5" value={targetRoas} onChange={(e) => setTargetRoas(e.target.value)} placeholder="target ROAS, e.g. 4 (optional)" className="gads-neg-modal__input" />
                </span>
              )}
              {strategy === 'MAXIMIZE_CLICKS' && (
                <span className="gads-wizard__inline">
                  <input type="number" inputMode="decimal" min="0" step="0.1" value={maxCpcCeiling} onChange={(e) => setMaxCpcCeiling(e.target.value)} placeholder="max CPC ceiling (optional)" className="gads-neg-modal__input" />
                </span>
              )}
              {strategy === 'TARGET_IMPRESSION_SHARE' && (
                <div className="gads-wizard__inline" style={{ marginTop: 6, gap: 6, flexWrap: 'wrap' }}>
                  <select value={tisLocation} onChange={(e) => setTisLocation(e.target.value)} className="gads-neg-modal__input">
                    {IS_LOCATIONS.map((l) => <option key={l.v} value={l.v}>{l.label}</option>)}
                  </select>
                  <input type="number" inputMode="decimal" min="1" max="100" step="1" value={tisFraction} onChange={(e) => setTisFraction(e.target.value)} placeholder="target % (e.g. 65)" className="gads-neg-modal__input" style={{ width: 130 }} />
                  <input type="number" inputMode="decimal" min="0" step="0.1" value={maxCpcCeiling} onChange={(e) => setMaxCpcCeiling(e.target.value)} placeholder="max CPC ceiling (opt.)" className="gads-neg-modal__input" />
                </div>
              )}
              {(strategy === 'MAXIMIZE_CONVERSIONS' || strategy === 'MAXIMIZE_CONVERSION_VALUE') && (
                <div className="gads-neg-modal__mt" style={{ marginTop: 6 }}>Needs conversion tracking on the account — the draft is refused otherwise.</div>
              )}
            </div>
            <div className="gads-rsa__section">
              <label className="gads-wizard__check"><input type="checkbox" checked={searchPartners} onChange={(e) => setSearchPartners(e.target.checked)} /> include Google search partners</label>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div className="gads-rsa__section">
              <label className="gads-rsa__label">Locations <span className="gads-neg-modal__mt">(one per line — resolved to Google geo targets at review)</span></label>
              <label className="gads-wizard__check" style={{ marginBottom: 6 }}><input type="checkbox" checked={worldwide} onChange={(e) => setWorldwide(e.target.checked)} /> target worldwide (no geo targeting)</label>
              {!worldwide && (
                <>
                  <textarea value={locationsText} onChange={(e) => setLocationsText(e.target.value)} placeholder={'Adelaide, South Australia\nSouth Australia'} className="gads-neg-modal__input gads-rsa__wide gads-wizard__ta" rows={3} />
                  <div className="gads-wizard__inline" style={{ marginTop: 6 }}>
                    <span className="gads-neg-modal__mt">country bias:</span>
                    <input type="text" maxLength={2} value={countryCode} onChange={(e) => setCountryCode(e.target.value.toUpperCase())} className="gads-neg-modal__input" style={{ width: 50 }} />
                  </div>
                </>
              )}
              {worldwide && <div className="gads-neg-modal__mt">⚠ The campaign will target every country — make sure that's intended.</div>}
            </div>
            <div className="gads-rsa__section">
              <label className="gads-rsa__label">Languages <span className="gads-neg-modal__mt">(who Google shows the ads to, by browser/account language)</span></label>
              <div className="gads-wizard__langs" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
                {COMMON_LANGUAGES.map((l) => (
                  <label key={l.id} className="gads-wizard__check">
                    <input
                      type="checkbox"
                      checked={languageIds.includes(l.id)}
                      onChange={(e) => setLanguageIds((prev) => e.target.checked ? Array.from(new Set([...prev, l.id])) : prev.filter((x) => x !== l.id))}
                    /> {l.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="gads-rsa__section">
              <label className="gads-rsa__label">Ad schedule <span className="gads-neg-modal__mt">(optional — empty = always on)</span></label>
              {schedule.map((r, i) => (
                <div key={i} className="gads-rsa__assetrow">
                  <select value={r.day} onChange={(e) => setSchedule((p) => p.map((x, j) => j === i ? { ...x, day: e.target.value } : x))} className="gads-neg-modal__input">
                    {DAYS.map((d) => <option key={d} value={d}>{d.toLowerCase()}</option>)}
                  </select>
                  <input type="number" inputMode="numeric" min="0" max="23" value={r.start} onChange={(e) => setSchedule((p) => p.map((x, j) => j === i ? { ...x, start: e.target.value } : x))} placeholder="8" className="gads-neg-modal__input" style={{ width: 64 }} />
                  <span className="gads-neg-modal__mt">to</span>
                  <input type="number" inputMode="numeric" min="1" max="24" value={r.end} onChange={(e) => setSchedule((p) => p.map((x, j) => j === i ? { ...x, end: e.target.value } : x))} placeholder="18" className="gads-neg-modal__input" style={{ width: 64 }} />
                  <button type="button" className="gads-deep__act-btn gads-deep__act-btn--danger" onClick={() => setSchedule((p) => p.filter((_, j) => j !== i))}>×</button>
                </div>
              ))}
              <button type="button" className="gads-deep__act-btn" onClick={() => setSchedule((p) => [...p, { day: 'MONDAY', start: '8', end: '18' }])}>+ schedule block</button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            {adGroups.map((g, i) => (
              <div key={i} className="gads-wizard__ag">
                <div className="gads-wizard__ag-head">
                  <strong>Ad group {i + 1}</strong>
                  {adGroups.length > 1 && <button type="button" className="gads-deep__act-btn gads-deep__act-btn--danger" onClick={() => setAdGroups((p) => p.filter((_, j) => j !== i))}>remove</button>}
                </div>
                <div className="gads-rsa__assetrow">
                  <input type="text" maxLength={255} value={g.name} onChange={(e) => patchAg(i, { name: e.target.value })} placeholder="ad group name" className="gads-neg-modal__input gads-rsa__wide" />
                  <input type="number" inputMode="decimal" min="0" step="0.05" value={g.maxCpc} onChange={(e) => patchAg(i, { maxCpc: e.target.value })} placeholder="max CPC (opt.)" className="gads-neg-modal__input gads-wizard__cpc" />
                </div>
                <div className="gads-rsa__assetrow">
                  <textarea value={g.keywordsText} onChange={(e) => patchAg(i, { keywordsText: e.target.value })} placeholder={'keywords — one per line\nemergency plumber\nblocked drain repair | exact\nleaking tap [phrase]'} className="gads-neg-modal__input gads-rsa__wide gads-wizard__ta" rows={3} />
                  <select value={g.matchType} onChange={(e) => patchAg(i, { matchType: e.target.value })} className="gads-neg-modal__input gads-rsa__pin" title="Default match type — override per keyword with inline syntax">
                    <option value="EXACT">exact</option>
                    <option value="PHRASE">phrase</option>
                    <option value="BROAD">broad</option>
                  </select>
                </div>
                <div className="gads-neg-modal__mt">Match type: uses the default above, or override per line — <code>plumber | exact</code> or <code>drain [phrase]</code>.</div>

                {/* Ads — up to 3 per ad group. F14. */}
                {g.ads.map((a, ai) => (
                  <div key={ai} className="gads-wizard__ad" style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #2a2a2a' }}>
                    <div className="gads-wizard__ag-head">
                      <strong>Ad {ai + 1}{g.ads.length > 1 ? ` / ${g.ads.length}` : ''}</strong>
                      {g.ads.length > 1 && <button type="button" className="gads-deep__act-btn gads-deep__act-btn--danger" onClick={() => patchAg(i, { ads: g.ads.filter((_, j) => j !== ai) })}>remove ad</button>}
                    </div>
                    <div className="gads-rsa__assetrow">
                      <input type="url" value={a.finalUrl} onChange={(e) => patchAd(i, ai, { finalUrl: e.target.value })} placeholder="https://client.com.au/landing-page" className="gads-neg-modal__input gads-rsa__wide" />
                      <input type="text" maxLength={15} value={a.path1} onChange={(e) => patchAd(i, ai, { path1: e.target.value })} placeholder="path1" className="gads-neg-modal__input gads-rsa__path" />
                      <input type="text" maxLength={15} value={a.path2} onChange={(e) => patchAd(i, ai, { path2: e.target.value })} placeholder="path2" className="gads-neg-modal__input gads-rsa__path" disabled={!a.path1.trim()} />
                    </div>
                    {a.headlines.map((h, hi) => (
                      <div key={hi} className="gads-rsa__assetrow">
                        <input type="text" maxLength={30} value={h} onChange={(e) => patchAd(i, ai, { headlines: a.headlines.map((x, j) => j === hi ? e.target.value : x) })} placeholder={`headline ${hi + 1}`} className="gads-neg-modal__input gads-rsa__wide" />
                        {counter(h, 30)}
                        {a.headlines.length > 3 && <button type="button" className="gads-deep__act-btn gads-deep__act-btn--danger" onClick={() => patchAd(i, ai, { headlines: a.headlines.filter((_, j) => j !== hi) })}>×</button>}
                      </div>
                    ))}
                    {a.headlines.length < 15 && <button type="button" className="gads-deep__act-btn" onClick={() => patchAd(i, ai, { headlines: [...a.headlines, ''] })}>+ headline</button>}
                    {a.descriptions.map((d, di) => (
                      <div key={di} className="gads-rsa__assetrow">
                        <input type="text" maxLength={90} value={d} onChange={(e) => patchAd(i, ai, { descriptions: a.descriptions.map((x, j) => j === di ? e.target.value : x) })} placeholder={`description ${di + 1}`} className="gads-neg-modal__input gads-rsa__wide" />
                        {counter(d, 90)}
                        {a.descriptions.length > 2 && <button type="button" className="gads-deep__act-btn gads-deep__act-btn--danger" onClick={() => patchAd(i, ai, { descriptions: a.descriptions.filter((_, j) => j !== di) })}>×</button>}
                      </div>
                    ))}
                    {a.descriptions.length < 4 && <button type="button" className="gads-deep__act-btn" onClick={() => patchAd(i, ai, { descriptions: [...a.descriptions, ''] })}>+ description</button>}
                  </div>
                ))}
                {g.ads.length < MAX_ADS_PER_GROUP && (
                  <button type="button" className="gads-deep__act-btn" style={{ marginTop: 8 }} onClick={() => patchAg(i, { ads: [...g.ads, newAd()] })}>+ ad</button>
                )}
              </div>
            ))}
            {adGroups.length < 20 && (
              <button type="button" className="gads-deep__act-btn" style={{ marginTop: 8 }} onClick={() => setAdGroups((p) => [...p, newAdGroup()])}>+ ad group</button>
            )}
            <div className="gads-neg-modal__mt" style={{ marginTop: 8 }}>Up to {MAX_ADS_PER_GROUP} ads per ad group here. Keyword match type: use the default above, or override per-keyword (e.g. <code>keyword | exact</code> for exact match).</div>
          </>
        )}

        {step === 3 && draft && (
          <>
            <div className="gads-rsa__section">
              <div className="gads-wizard__summary">{draft.summary}</div>
              {draft.locationNotes.length > 0 && (
                <div className="gads-wizard__locs">
                  <div className="gads-rsa__label">Resolved targeting — confirm this is what you meant:</div>
                  {draft.locationNotes.map((n, i) => <div key={i} className="gads-wizard__loc">📍 {n}</div>)}
                </div>
              )}
              {draft.validationOk ? (
                <div className="gads-wizard__valid">✓ The full build passed Google's dry-run validation (nothing has been created yet).</div>
              ) : (
                <div className="ads-portfolio__change-warn">✗ Validation failed: {draft.validationErrors.join('; ')} — go back and fix, then re-review.</div>
              )}
            </div>
            <div className="gads-neg-modal__warn">
              Creates the campaign <strong>PAUSED</strong> with all ad groups, keywords and ads inside. It will not serve or spend until you explicitly enable it. Undo from “change activity” at the bottom of this page removes the whole campaign permanently.
            </div>
          </>
        )}

        <div className="gads-neg-modal__actions">
          {done ? (
            <button type="button" className="gads-neg-modal__apply" onClick={onClose}>Close</button>
          ) : (
            <>
              <button type="button" className="gads-neg-modal__cancel" disabled={drafting || creating} onClick={close}>Cancel</button>
              {step > 0 && <button type="button" className="gads-neg-modal__cancel" disabled={drafting || creating} onClick={() => setStep(step - 1)}>← Back</button>}
              {step < 3 && <button type="button" className="gads-neg-modal__apply" disabled={drafting} onClick={goNext}>{drafting ? 'validating…' : step === 2 ? 'Validate & review →' : 'Next →'}</button>}
              {step === 3 && <button type="button" className="gads-neg-modal__apply" disabled={creating || !draft?.validationOk} onClick={create}>{creating ? 'creating…' : 'Create campaign (paused)'}</button>}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
